import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, AdminApiError, AdminReadTimeoutError } from '../../api/admin.ts';
import { clearAdminQueries } from '../../state/admin-query-cache.ts';
import { ADMIN_AUTH_CHANGE_KEY, announceAdminAuthChange } from '../../state/admin-auth-change.ts';
import { Alert, Button, ConfirmDialog, LoadingBlock } from '../ui.tsx';
import { AppShell, type ShellNavGroup, type ShellNavItem } from '../app-shell.tsx';

/**
 * Shell for the operator CMS. English-only on purpose — this is an internal
 * tool for the deployment's operators, not a customer surface, so none of it
 * goes through i18next.
 *
 * The gate here is UX, not security: the API refuses anything without an
 * admin session regardless of what the SPA renders.
 */

export function useAdminMe() {
  return useQuery({
    queryKey: ['admin', 'me'],
    queryFn: ({ signal }) => adminApi.me(signal),
    retry: (failureCount, error) =>
      !(error instanceof AdminReadTimeoutError) &&
      !(error instanceof AdminApiError && error.isUnauthenticated) &&
      failureCount < 2,
    staleTime: 5 * 60 * 1000,
  });
}

export function RequireAdmin({ children }: { children: ReactNode }) {
  const me = useAdminMe();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [renderedEmail, setRenderedEmail] = useState<string | null>(null);
  const announcedIdentity = useRef(false);
  const [checkingSession, setCheckingSession] = useState(false);

  useEffect(() => {
    let mounted = true;
    let generation = 0;
    let focusTimer: ReturnType<typeof setTimeout> | null = null;
    const beginRevalidation = () => {
      const current = ++generation;
      // Both the gate and cancellation start on the wake-up itself. Delaying
      // either until the focus debounce fires would let an old A /me answer
      // redirect or reveal A data under B's cookie in that interval.
      setCheckingSession(true);
      const cancellation = queryClient.cancelQueries({ queryKey: ['admin', 'me'], exact: true });
      return async () => {
        try {
          await cancellation;
          if (!mounted || current !== generation) return;
          await queryClient.refetchQueries({ queryKey: ['admin', 'me'], exact: true, type: 'active' });
        } finally {
          if (mounted && current === generation) setCheckingSession(false);
        }
      };
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key !== ADMIN_AUTH_CHANGE_KEY || event.newValue === null) return;
      if (focusTimer !== null) clearTimeout(focusTimer);
      focusTimer = null;
      void beginRevalidation()();
    };
    const onFocus = () => {
      // Visibility and window-focus often arrive together. Coalesce them,
      // while never dropping a later focus behind an old pending /me.
      if (focusTimer !== null) return;
      const completeRevalidation = beginRevalidation();
      focusTimer = setTimeout(() => {
        focusTimer = null;
        void completeRevalidation();
      }, 50);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') onFocus();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      mounted = false;
      generation += 1;
      if (focusTimer !== null) clearTimeout(focusTimer);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [queryClient]);

  useLayoutEffect(() => {
    if (me.error instanceof AdminApiError && me.error.isUnauthenticated) {
      // Keep the 401 until the redirect commits; remove every other operator
      // response so a later identity cannot see the previous admin's data.
      clearAdminQueries(queryClient, true);
    } else if (me.data?.email) {
      if (renderedEmail !== me.data.email) {
        if (renderedEmail) {
          // The previous admin subtree is concealed in this render and is
          // remounted under the new email before paint. Remove every old
          // response first so its replacement cannot inherit A's data.
          clearAdminQueries(queryClient, true);
        }
        setRenderedEmail(me.data.email);
      }
      if (!me.isFetching && !announcedIdentity.current) {
        announcedIdentity.current = true;
        announceAdminAuthChange();
      }
    }
  }, [me.data?.email, me.error, me.isFetching, queryClient, renderedEmail]);

  if (me.isLoading) return <LoadingBlock label="Checking admin session…" />;
  // Only a 401 is "not signed in". A network failure or a 500 is "could not
  // tell", and redirecting on that would dump a working operator onto the
  // sign-in page over a blip — and is one half of the guard-pair loop the
  // partner portal documents (see RequirePartner): a redirect on "could not
  // tell" is exactly what leaves a browser flashing between two screens.
  if (me.error instanceof AdminApiError && me.error.isUnauthenticated) {
    return <Navigate to="/admin/login" replace state={{ from: location.pathname + location.search }} />;
  }
  const reachabilityError = (
    <main className="mx-auto max-w-md px-4 py-24">
      <Alert tone="error" title="The admin console could not be reached">
        {me.error instanceof Error ? me.error.message : 'Please try again in a moment.'}
      </Alert>
      <Button className="mt-4" onClick={() => void me.refetch()}>
        Try again
      </Button>
    </main>
  );
  if (!me.data) {
    return reachabilityError;
  }
  const blocked =
    checkingSession || me.isFetching || me.isError || (renderedEmail !== null && renderedEmail !== me.data.email);
  return (
    <>
      {checkingSession || me.isFetching ? (
        <LoadingBlock label="Checking admin session…" />
      ) : me.isError ? (
        reachabilityError
      ) : null}
      {/* Keep in-progress editor state mounted; conceal and disable it until
          /me has verified the cookie currently in this browser. */}
      <div key={renderedEmail ?? me.data.email} hidden={blocked} inert={blocked} aria-hidden={blocked}>
        {children}
      </div>
    </>
  );
}

/**
 * The console's navigation, in the app shell every audience shares
 * (components/app-shell.tsx). The office's group names do not fit an
 * operator's work, so it keeps the shell's order — Overview alone at the
 * top, labelled groups, the operators themselves pinned at the foot — with
 * labels of its own: the tenants (Accounts), the resellers (Partners), and
 * the product itself (Product).
 */
const NAV_ITEMS: ReadonlyArray<ShellNavItem> = [
  { to: '/admin', label: 'Overview', icon: 'home', group: 'home' },
  { to: '/admin/companies', label: 'Companies', icon: 'company', group: 'accounts' },
  { to: '/admin/impersonate', label: 'Impersonate', icon: 'impersonate', group: 'accounts' },
  { to: '/admin/discounts', label: 'Discounts', icon: 'tag', group: 'accounts' },
  { to: '/admin/partners', label: 'Partners', icon: 'team', group: 'partners' },
  { to: '/admin/partners/payouts', label: 'Partner payouts', icon: 'invoice', group: 'partners' },
  { to: '/admin/feedback', label: 'Product Feedback', icon: 'feedback', group: 'product' },
  { to: '/admin/feedback/settings', label: 'Feedback settings', icon: 'settings', group: 'product' },
  { to: '/admin/public-demo', label: 'Live demo', icon: 'monitor', group: 'product' },
  { to: '/admin/admins', label: 'Admins', icon: 'users', group: 'footer' },
  { to: '/admin/security', label: 'Security', icon: 'shield', group: 'footer' },
];

const NAV_GROUPS: ReadonlyArray<ShellNavGroup> = [
  { id: 'accounts', label: 'Accounts' },
  { id: 'partners', label: 'Partners' },
  { id: 'product', label: 'Product' },
];

/** Desktop-first, but a phone still gets the four an operator reaches for. */
const PHONE_TAB_ORDER: ReadonlyArray<string> = ['/admin', '/admin/companies', '/admin/impersonate', '/admin/partners'];

const BRAND = { to: '/admin', label: 'Admin' };

export default function AdminLayout() {
  const me = useAdminMe();
  const queryClient = useQueryClient();
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState<string | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);

  async function handleSignOut() {
    setSigningOut(true);
    setSignOutError(null);
    try {
      await adminApi.logout();
    } catch (caught) {
      // A 401 means there was no live session to end, which is what was
      // wanted. Anything else — offline, a refused CSRF token, a Worker error —
      // means the HttpOnly cookie and the server-side session are both still
      // live. Leaving for the sign-in page then would show a sign-out that did
      // not happen, and on a shared device the next person to reload this tab
      // would be signed straight back in. So the dialog stays up with the
      // error, and the retry is the same button.
      if (!(caught instanceof AdminApiError && caught.isUnauthenticated)) {
        setSigningOut(false);
        setSignOutError(
          caught instanceof AdminApiError
            ? `Could not sign you out: ${caught.message}`
            : 'Could not sign you out. Check your connection and try again.',
        );
        return;
      }
    }
    clearAdminQueries(queryClient);
    announceAdminAuthChange();
    // A hard navigation, so every admin query cache resets with the cookies.
    window.location.assign('/admin/login');
  }

  return (
    <AppShell
      items={NAV_ITEMS}
      groups={NAV_GROUPS}
      phoneTabOrder={PHONE_TAB_ORDER}
      brand={BRAND}
      moreOpen={moreOpen}
      onMoreOpenChange={setMoreOpen}
      mainClassName="mx-auto max-w-6xl px-4 py-6"
      topBar={
        <>
          <p className="min-w-0 flex-1 basis-0 truncate text-lg font-bold text-[color:var(--color-brand)]">Admin</p>
          <div className="ml-auto flex shrink-0 items-center gap-3">
            <span className="hidden text-sm text-[color:var(--color-muted)] sm:inline">{me.data?.email}</span>
            {/* Asked first: this button sits at the end of the same header row
                the operator uses all day, and an accidental hit takes the page
                they were on with it — the sign-out is a hard navigation. */}
            <Button variant="ghost" busy={signingOut} onClick={() => setConfirmingSignOut(true)}>
              Sign out
            </Button>
            {confirmingSignOut ? (
              <ConfirmDialog
                title="Sign out of the admin console?"
                confirmLabel="Sign out"
                cancelLabel="Stay signed in"
                busy={signingOut}
                error={signOutError}
                onCancel={() => {
                  setConfirmingSignOut(false);
                  setSignOutError(null);
                }}
                onConfirm={() => void handleSignOut()}
              >
                <p>This ends your admin session on this device and returns you to the sign-in page.</p>
              </ConfirmDialog>
            ) : null}
          </div>
        </>
      }
    >
      <Outlet />
    </AppShell>
  );
}
